/**
 * @module SwitchGroup
 *
 * @see https://0.vuetifyjs.com/components/forms/switch
 *
 * @remarks
 * Group component for managing multiple switches with tri-state support.
 * Provides group context to child Switch.Root components. Supports batch
 * operations (selectAll, unselectAll, toggleAll) and mixed/indeterminate states.
 */

<script lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Composables
  import { createContext } from '#v0/composables/createContext'
  import { createGroup } from '#v0/composables/createGroup'
  import { useProxyModel } from '#v0/composables/useProxyModel'

  // Transformers
  import { toElement } from '#v0/composables/toElement'

  // Utilities
  import { isUndefined } from '#v0/utilities'
  import { toRef, toValue } from 'vue'

  // Types
  import type { AtomProps } from '#v0/components/Atom'
  import type { GroupContext, GroupTicket } from '#v0/composables/createGroup'
  import type { ID } from '#v0/types'
  import type { MaybeRefOrGetter } from 'vue'

  /** Ticket for switch items with element reference for focus management */
  export interface SwitchTicket extends GroupTicket {
    /** Element reference for group focus management */
    el?: MaybeRefOrGetter<Element | null | undefined>
  }

  /** Imperative handle exposed by Switch.Group. */
  export interface SwitchGroupExpose {
    /** Focus the selected switch, or the first if none is selected. */
    focus: (options?: FocusOptions) => void
  }

  export interface SwitchGroupProps extends AtomProps {
    /** Namespace for context provision to children */
    namespace?: string
    /** Disables all switches in the group */
    disabled?: boolean
    /** Auto-select items on registration */
    enroll?: boolean
    /** Require at least one switch to be on. `'force'` prevents deselecting the last item */
    mandatory?: boolean | 'force'
    /** Accessible group label */
    label?: string
    /** ID of element that labels this group */
    ariaLabelledby?: string
    /** ID of element that describes this group */
    ariaDescribedby?: string
  }

  export interface SwitchGroupSlotProps {
    /** Whether the group is disabled */
    isDisabled: boolean
    /** Whether no switches are on */
    isNoneSelected: boolean
    /** Whether all switches are on */
    isAllSelected: boolean
    /** Whether some but not all switches are on */
    isMixed: boolean
    /** Turn on specific switches by ID */
    select: (id: ID | ID[]) => void
    /** Turn off specific switches by ID */
    unselect: (id: ID | ID[]) => void
    /** Toggle specific switches by ID */
    toggle: (id: ID | ID[]) => void
    /** Turn on all switches */
    selectAll: () => void
    /** Turn off all switches */
    unselectAll: () => void
    /** Toggle all switches */
    toggleAll: () => void
    /** Pre-computed ARIA attributes for the group element */
    attrs: {
      'role': 'group'
      'aria-label': string | undefined
      'aria-labelledby': string | undefined
      'aria-describedby': string | undefined
    }
  }

  export const [useSwitchGroup, provideSwitchGroup] = /* @__PURE__ */ createContext<GroupContext<SwitchTicket>>()
</script>

<script lang="ts" setup generic="T = unknown">
  defineOptions({ name: 'SwitchGroup' })

  defineSlots<{
    default: (props: SwitchGroupSlotProps) => any
  }>()

  defineEmits<{
    'update:model-value': [value: T | T[]]
  }>()

  const {
    as = 'div',
    renderless,
    namespace = 'v0:switch:group',
    ariaLabelledby,
    ariaDescribedby,
    disabled = false,
    enroll = false,
    mandatory = false,
    label,
  } = defineProps<SwitchGroupProps>()

  const model = defineModel<T | T[]>()

  const group = createGroup<SwitchTicket>({
    disabled: toRef(() => disabled),
    enroll: toRef(() => enroll),
    mandatory: toRef(() => mandatory),
    events: true,
  })

  useProxyModel(group, model, { multiple: true })

  provideSwitchGroup(namespace, group)

  function focus (options?: FocusOptions) {
    const id = [...group.selectedIds][0]
    const ticket = isUndefined(id) ? group.seek('first') : group.get(id)
    ;(toElement(toValue(ticket?.el)) as HTMLElement | undefined)?.focus(options)
  }

  defineExpose<SwitchGroupExpose>({ focus })

  const slotProps = toRef((): SwitchGroupSlotProps => ({
    isDisabled: toValue(group.disabled),
    isNoneSelected: group.isNoneSelected.value,
    isAllSelected: group.isAllSelected.value,
    isMixed: group.isMixed.value,
    select: group.select,
    unselect: group.unselect,
    toggle: group.toggle,
    selectAll: group.selectAll,
    unselectAll: group.unselectAll,
    toggleAll: group.toggleAll,
    attrs: {
      'role': 'group',
      'aria-label': label || undefined,
      'aria-labelledby': ariaLabelledby || undefined,
      'aria-describedby': ariaDescribedby || undefined,
    },
  }))
</script>

<template>
  <Atom
    v-bind="slotProps.attrs"
    :as
    :renderless
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
